import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import type {
    DataTableColumn,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import {
    DataTableCustomized,
    FilterBar,
    createColumns,
    createDateFormatter,
    createInternalLinkFormatter,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    NEGOTIATION_PRIORITY,
    NEGOTIATION_STAGES,
    NEGOTIATION_STATUS,
    NEGOTIATION_TYPES,
} from '@/modules/Customer/constants'
import { ContractNegotiation } from '@/modules/Customer/types'
import { getEmployeesOptionsPaginated } from '@/modules/Employee/services'
import { formatCurrency } from '@/utils/function'
import { paths } from '@/utils/path'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Chip } from 'vnetwork-ui'
import { Color } from 'vnetwork-ui/dist/types'
import { getAllContractNegotiations } from '../../services'

// ============================================================================
// Filter config
// ============================================================================

interface NegotiationFilters {
    status: string[]
    priority: string[]
    type: string[]
    stage: string[]
    assignees: string[]
    created_at: string[]
    created_at_to: string
    deadline: string[]
    deadline_to: string
}

const FILTER_DEFAULTS: NegotiationFilters = {
    status: [],
    priority: [],
    type: [],
    stage: [],
    assignees: [],
    created_at: [],
    created_at_to: '',
    deadline: [],
    deadline_to: '',
}

const createNegotiationFilters = (t: any): FilterItemConfig[] => [
    {
        name: 'status',
        type: 'multi-select',
        label: t('negotiations.status'),
        options: NEGOTIATION_STATUS(t)?.map((item) => ({
            label: item.label,
            value: item.value,
        })),
        placeholder: t('negotiations.status'),
    },
    {
        name: 'priority',
        type: 'multi-select',
        label: t('negotiations.priority'),
        options: NEGOTIATION_PRIORITY(t)?.map((item) => ({
            label: item.label,
            value: item.value,
        })),
        placeholder: t('negotiations.priority'),
    },
    {
        name: 'type',
        type: 'multi-select',
        label: t('negotiations.type'),
        options: NEGOTIATION_TYPES(t)?.map((item) => ({
            label: item.label,
            value: item.value,
        })),
        placeholder: t('negotiations.type'),
    },
    {
        name: 'stage',
        type: 'multi-select',
        label: t('negotiations.stage'),
        options: NEGOTIATION_STAGES(t)?.map((item) => ({
            label: item.label,
            value: item.value,
        })),
        placeholder: t('negotiations.stage'),
    },
    {
        name: 'assignees',
        type: 'async-select',
        label: t('negotiations.assignees'),
        asyncOptions: (query, page) =>
            getEmployeesOptionsPaginated(query, undefined, 'active', page),
        multiple: true,
        placeholder: t('negotiations.assignees'),
    },
    {
        name: 'created_at',
        type: 'daterangepicker',
        names: ['created_at', 'created_at_to'],
        label: t('negotiations.created_at'),
        placeholder: t('negotiations.created_at'),
    },
    {
        name: 'deadline',
        type: 'daterangepicker',
        names: ['deadline', 'deadline_to'],
        label: t('negotiations.deadline'),
        placeholder: t('negotiations.deadline'),
    },
]

// ============================================================================
// Columns
// ============================================================================

const createNegotiationColumns = (
    t: any
): DataTableColumn<ContractNegotiation>[] =>
    createColumns([
        {
            key: 'contract_negotiation_code',
            title: t('negotiations.code'),
            width: 150,
            cellClassName: 'text-primary truncate',
            formatter: createInternalLinkFormatter({
                pathKey: 'contractNegotiationDetail',
                pathParams: (_, row) => ({
                    id: String(row.contract_negotiation_id),
                }),
            }),
        },
        {
            key: 'contract_negotiation_title',
            title: t('negotiations.title'),
            width: 250,
            cellClassName: 'truncate',
        },
        {
            key: 'contract_negotiation_status',
            title: t('negotiations.status'),
            width: 250,
            render: (value) => {
                const status = NEGOTIATION_STATUS(t)?.find(
                    (s) => s.value === value
                )
                return status ? (
                    <Chip
                        label={status.label || (value as string)}
                        color={status.color as Color}
                        size="small"
                    />
                ) : (
                    (value as string) || '-'
                )
            },
        },
        {
            key: 'contract_negotiation_priority',
            title: t('negotiations.priority'),
            width: 120,
            render: (value) => {
                const priority = NEGOTIATION_PRIORITY(t)?.find(
                    (p) => p.value === value
                )
                return priority ? (
                    <Chip
                        label={priority.label || (value as string)}
                        color={priority.color as Color}
                        size="small"
                    />
                ) : (
                    (value as string) || '-'
                )
            },
        },
        {
            key: 'contract_negotiation_type',
            title: t('negotiations.type'),
            width: 150,
            render: (value) => {
                const type = NEGOTIATION_TYPES(t)?.find(
                    (item) => item.value === value
                )
                return (
                    <span className="capitalize">
                        {type?.label || (value as string)}
                    </span>
                )
            },
        },
        {
            key: 'contract_negotiation_stage',
            title: t('negotiations.stage'),
            width: 180,
            render: (value) => {
                const stage = NEGOTIATION_STAGES(t)?.find(
                    (s) => s.value === value
                )
                return stage ? (
                    <Chip
                        label={stage.label || (value as string)}
                        color={stage.color as Color}
                        size="small"
                    />
                ) : (
                    (value as string) || '-'
                )
            },
        },
        {
            key: 'contract_negotiation_value',
            title: t('negotiations.value'),
            width: 150,
            cellClassName: 'text-right',
            render: (value) => (
                <span>{formatCurrency((value as number) || 0)}</span>
            ),
        },
        {
            key: 'contract_negotiation_progress',
            title: t('negotiations.progress'),
            width: 100,
            cellClassName: 'text-center',
            render: (value) => <span>{(value as number) || 0}%</span>,
        },
        {
            key: 'assignee_names',
            title: t('negotiations.assignees'),
            width: 200,
            render: (value) => {
                const names = (value as string[])?.join(', ')
                return (
                    <span className="truncate text-xs" title={names}>
                        {names || '-'}
                    </span>
                )
            },
        },
        {
            key: 'creator_name',
            title: t('negotiations.creator'),
            width: 150,
        },
        {
            key: 'contract_negotiation_deadline',
            title: t('negotiations.deadline'),
            width: 120,
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
        {
            key: 'created_at',
            title: t('negotiations.created_at'),
            width: 120,
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
    ])

// ============================================================================
// Component
// ============================================================================

const List: React.FC = () => {
    const { t } = useTranslation()

    const {
        params,
        queryParams,
        filters,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<NegotiationFilters>({
        defaults: {
            sort_by: 'created_at',
            sort_order: 'desc',
            page_size: 20,
        },
        filterDefaults: FILTER_DEFAULTS,
    })

    const { control } = useForm<NegotiationFilters>({
        defaultValues: filters,
    })

    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.NEGOTIATIONS, queryParams],
        queryFn: () => getAllContractNegotiations(queryParams),
        placeholderData: true,
    })

    const columns = useMemo(() => createNegotiationColumns(t), [t])
    const filterItems = useMemo(() => createNegotiationFilters(t), [t])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    const negotiations = data?.results || []
    const pagination = data?.pagination
    const stateComponent = renderState()

    return (
        <>
            <SubHeaders
                breadcrumbs={[
                    {
                        title: t('contract-negotiation'),
                        path: paths.contractNegotiation,
                    },
                ]}
            />

            <Paper className="mt-2">
                <FilterBar
                    control={control}
                    setFilter={setFilter}
                    items={filterItems}
                    hideSearch
                />

                {stateComponent || (
                    <DataTableCustomized<ContractNegotiation>
                        columns={columns}
                        rows={negotiations}
                        rowKey={(row) => String(row.contract_negotiation_id)}
                        size="small"
                        resizable
                        isFetching={isFetching}
                        emptyMessage={t('no-negotiations')}
                        sorting={{
                            sortBy: params.sort_by,
                            sortOrder: params.sort_order,
                            onSortChange: handleSortChange,
                        }}
                        pagination={{
                            page: params.page || 1,
                            pageSize: params.page_size || 20,
                            total: pagination?.total || 0,
                            onPageChange: handlePageChange,
                            onPageSizeChange: handlePageSizeChange,
                            pageSizeOptions: [10, 20, 50, 100],
                        }}
                        toolbar={{
                            search: {
                                ...searchInputProps,
                                placeholder: t('search'),
                            },
                            settings: {
                                show: true,
                                storageKey: 'contract-negotiations',
                            },
                        }}
                    />
                )}
            </Paper>
        </>
    )
}

export default List
